import React, { createContext, useContext, useEffect, useState } from 'react';
import { app, authentication } from '@microsoft/teams-js';

interface TeamAppContextType {
  isInTeams: boolean;
  isLoading: boolean;
  msTeamsAuthToken: string | null;
}

const TeamAppContext = createContext<TeamAppContextType>({
  isInTeams: false,
  isLoading: true,
  msTeamsAuthToken: null,
});

export const useTeamApp = () => useContext(TeamAppContext);

export const TeamAppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isInTeams, setIsInTeams] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [msTeamsAuthToken, setMsTeamsAuthToken] = useState<string | null>(null);

  useEffect(() => {
    const checkTeamsEnvironment = async () => {
      try {
        // Initialize Teams SDK
        await app.initialize();
        console.log(
          '%c[Teams]%c App is running in Microsoft Teams',
          'background: #0078d4; color: white; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
          'color: #0078d4; font-weight: bold; margin-left: 5px;'
        );
        
        // Check if we're running in Teams
        const context = await app.getContext();
        setIsInTeams(true);

        // Get Teams token
        const token = await authentication.getAuthToken();
        setMsTeamsAuthToken(token);
      } catch (error) {
        // If initialization fails, we're not in Teams
        setIsInTeams(false);
        setMsTeamsAuthToken(null);
        console.log(
            '%c[Teams]%c App is not running in Microsoft Teams',
            'background: #ff0000; color: white; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
            'color: #ff0000; font-weight: bold; margin-left: 5px;'
        );
      } finally {
        setIsLoading(false);
      }
    };

    checkTeamsEnvironment();
  }, []);

  return (
    <TeamAppContext.Provider value={{ isInTeams, isLoading, msTeamsAuthToken }}>
      {children}
    </TeamAppContext.Provider>
  );
}; 